const DERIV_APP_ID = '33BElOXrjjUYMBwFon5g8';

export const DERIV_REST_BASE_URL = 'https://api.derivws.com';

export const getPatAppId = () => DERIV_APP_ID;

// ─── OAuth2 + PKCE ("Sign in with Deriv") ────────────────────────────────────

const OAUTH_STORAGE_TTL = 10 * 60 * 1000;
const OAUTH_STATE_KEY = 'oauth_state';
const OAUTH_STATE_TIMESTAMP_KEY = 'oauth_state_timestamp';
const OAUTH_CODE_VERIFIER_KEY = 'oauth_code_verifier';
const OAUTH_CODE_VERIFIER_TIMESTAMP_KEY = 'oauth_code_verifier_timestamp';

const DERIV_OAUTH_CONFIG = {
  AUTHORIZATION_URL: 'https://auth.deriv.com/oauth2/auth',
  TOKEN_EXCHANGE_ENDPOINT: process.env.REACT_APP_DERIV_OAUTH_TOKEN_EXCHANGE_ENDPOINT || '/api/oauth/token',
  CLIENT_ID: DERIV_APP_ID,
  SCOPE: process.env.REACT_APP_DERIV_OAUTH_SCOPE || 'application_read',
};

const base64UrlEncode = (bytes) => {
  const binary = String.fromCharCode(...bytes);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const generateRandomValue = (byteLength) => {
  const bytes = new Uint8Array(byteLength);
  window.crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
};

const generateCodeChallenge = async (codeVerifier) => {
  if (!window.crypto?.subtle) {
    throw new Error('PKCE needs crypto.subtle. Use HTTPS or localhost for Deriv OAuth.');
  }

  const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier));
  return base64UrlEncode(new Uint8Array(digest));
};

const storeOAuthValue = (key, timestampKey, value) => {
  window.sessionStorage.setItem(key, value);
  window.sessionStorage.setItem(timestampKey, Date.now().toString());
};

const readOAuthValue = (key, timestampKey) => {
  const value = window.sessionStorage.getItem(key);
  const timestamp = Number(window.sessionStorage.getItem(timestampKey));

  if (!value || !timestamp || Date.now() - timestamp > OAUTH_STORAGE_TTL) {
    window.sessionStorage.removeItem(key);
    window.sessionStorage.removeItem(timestampKey);
    return null;
  }

  return value;
};

export const getOAuthRedirectURI = () => {
  if (process.env.REACT_APP_DERIV_OAUTH_REDIRECT_URI) {
    return process.env.REACT_APP_DERIV_OAUTH_REDIRECT_URI;
  }

  return `${window.location.origin}/callback`;
};

export const getCodeVerifier = () => readOAuthValue(OAUTH_CODE_VERIFIER_KEY, OAUTH_CODE_VERIFIER_TIMESTAMP_KEY);

export const validateOAuthState = (returnedState) => {
  const storedState = readOAuthValue(OAUTH_STATE_KEY, OAUTH_STATE_TIMESTAMP_KEY);
  return !!returnedState && returnedState === storedState;
};

export const clearOAuthTransaction = () => {
  window.sessionStorage.removeItem(OAUTH_STATE_KEY);
  window.sessionStorage.removeItem(OAUTH_STATE_TIMESTAMP_KEY);
  window.sessionStorage.removeItem(OAUTH_CODE_VERIFIER_KEY);
  window.sessionStorage.removeItem(OAUTH_CODE_VERIFIER_TIMESTAMP_KEY);
};

export const generateOAuthURL = async () => {
  const clientId = DERIV_OAUTH_CONFIG.CLIENT_ID;
  const redirectUri = getOAuthRedirectURI();

  if (!clientId) {
    throw new Error('Deriv OAuth client_id is missing.');
  }

  const state = generateRandomValue(24);
  const codeVerifier = generateRandomValue(64);
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  storeOAuthValue(OAUTH_STATE_KEY, OAUTH_STATE_TIMESTAMP_KEY, state);
  storeOAuthValue(OAUTH_CODE_VERIFIER_KEY, OAUTH_CODE_VERIFIER_TIMESTAMP_KEY, codeVerifier);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: clientId,
    redirect_uri: redirectUri,
    scope: DERIV_OAUTH_CONFIG.SCOPE,
    state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  });

  return `${DERIV_OAUTH_CONFIG.AUTHORIZATION_URL}?${params.toString()}`;
};

const getTokenExchangeError = (body, fallback) => {
  if (body?.error_description) return body.error_description;
  if (typeof body?.error === 'string') return body.error;
  if (body?.errors?.[0]?.message) return body.errors[0].message;
  return fallback;
};

/** Exchanges a Deriv OAuth authorization code for an access token via the serverless proxy at /api/oauth/token. */
export const exchangeOAuthCode = async (code, codeVerifier) => {
  const response = await fetch(DERIV_OAUTH_CONFIG.TOKEN_EXCHANGE_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      code,
      codeVerifier,
      clientId: DERIV_OAUTH_CONFIG.CLIENT_ID,
      redirectUri: getOAuthRedirectURI(),
    }),
  });

  const body = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(getTokenExchangeError(body, 'Deriv OAuth token exchange failed.'));
  }

  if (!body.access_token) {
    throw new Error('Deriv OAuth token exchange did not return an access token.');
  }

  return body;
};
